/**
 * 远程控制连接状态管理 Store。
 */

import type { Socket } from "socket.io-client";
import { create } from "zustand";
import { leaveRoom, sendPing } from "@/services/remote/signaling";

type HeartbeatPayload = {
	from?: string;
	timestamp: number;
};

type UserDisconnectedPayload = {
	userId: string;
};

type RoomUsersPayload = {
	users: string[];
};

type ConnectionStoreState = {
	currentRoomId: string | null;
	setCurrentRoomId: (roomId: string | null) => void;
	participants: string[];
	setParticipants: (participants: string[]) => void;
	isOtherParticipantOnline: boolean;
	setOtherParticipantOnline: (status: boolean) => void;
	lastHeartbeat: number | null;
	setLastHeartbeat: (time: number | null) => void;
	socket: Socket | null;
	setSocket: (socket: Socket | null) => void;
	setupSocketListeners: (socket: Socket | null) => void;
	startHeartbeat: () => void;
	stopHeartbeat: () => void;
	heartbeatInterval: ReturnType<typeof setInterval> | null;
	resetRoom: () => void;
};

const useConnectionStore = create<ConnectionStoreState>((set, get) => ({
	// 当前房间 ID
	currentRoomId: null,
	setCurrentRoomId: (roomId) => set({ currentRoomId: roomId }),

	// 参与者列表
	participants: [],
	setParticipants: (participants) => set({ participants }),

	// 其他参与者在线状态
	isOtherParticipantOnline: false,
	setOtherParticipantOnline: (status) =>
		set({ isOtherParticipantOnline: status }),

	// 最后心跳时间
	lastHeartbeat: null,
	setLastHeartbeat: (time) => set({ lastHeartbeat: time }),

	// Socket 实例
	socket: null,

	setSocket: (socket) => {
		const store = get();
		if (socket === store.socket) return;

		set({ socket });
		store.setupSocketListeners(socket);
	},

	// 设置 Socket 监听器
	setupSocketListeners: (socket) => {
		if (!socket) return;

		// 心跳监听
		socket.on("heartbeat", ({ timestamp }: HeartbeatPayload) => {
			set({
				isOtherParticipantOnline: true,
				lastHeartbeat: timestamp,
			});
		});

		// 用户断开连接监听
		socket.on("user-disconnected", ({ userId }: UserDisconnectedPayload) => {
			const store = get();
			set({
				participants: store.participants.filter((id) => id !== userId),
				isOtherParticipantOnline: false,
			});
		});

		// 房间用户更新监听
		socket.on("room-users", ({ users }: RoomUsersPayload) => {
			set({
				participants: users,
				isOtherParticipantOnline: users.length > 1,
			});
		});
	},

	// 启动心跳
	startHeartbeat: () => {
		const store = get();
		if (!store.currentRoomId) {
			console.log(
				"start heartbeat failed, socket or currentRoomId is null",
				store.currentRoomId,
			);
			return;
		}

		// 清理旧的心跳定时器
		if (store.heartbeatInterval) {
			clearInterval(store.heartbeatInterval);
		}

		get().setLastHeartbeat(Date.now());

		// 设置新的心跳定时器
		const heartbeatInterval = setInterval(() => {
			sendPing();
			const currentStore = get(); // 获取最新的 store 状态

			const lastHeartbeat = currentStore.lastHeartbeat ?? Date.now();
			const delay = Date.now() - lastHeartbeat;

			console.log(
				"last time:",
				currentStore.lastHeartbeat,
				"delay time:",
				delay,
			);

			// 三段式判定，区间必须连续无空档：
			//   - < 10s：心跳新鲜，对端在线
			//   - 10s ~ 20s：心跳变旧，标记离线但保留连接，等待恢复
			//   - > 20s：判定掉线，主动离开房间
			// 阈值取 10s（两个心跳周期）而非 5s：心跳周期本身就是 5s，tick 时
			// delay 天然在 5s 附近徘徊（≈ 周期 - RTT，且 setInterval 有漂移），
			// 若用 5s 当界，正常在线的对端会被反复误标离线。
			if (delay < 10000) {
				currentStore.setOtherParticipantOnline(true);
			} else if (delay <= 20000) {
				console.log("heartbeat stale, set other participant offline");
				currentStore.setOtherParticipantOnline(false);
			} else {
				console.log("heartbeat timeout over 20s, leaving room");
				currentStore.setOtherParticipantOnline(false);
				leaveRoom();
			}
		}, 5000);
		console.log("start heartbeat success");
		set({ heartbeatInterval });
	},

	// 停止心跳
	stopHeartbeat: () => {
		const store = get();
		if (store.heartbeatInterval) {
			clearInterval(store.heartbeatInterval);
			set({ heartbeatInterval: null });
		}
	},

	// 心跳定时器
	heartbeatInterval: null,

	// 重置所有状态
	resetRoom: () => {
		console.log("[signal room store]:resetRoom");
		const store = get();
		store.stopHeartbeat();
		set({
			currentRoomId: null,
			participants: [],
			isOtherParticipantOnline: false,
			lastHeartbeat: null,
		});
	},
}));

export default useConnectionStore;
